/**
 * ステージ定義と抽選テーブルの検証（純粋ロジック / Phaser 非依存）。
 * 不正な設定を早期に検知して、開発中の事故を防ぐ。
 */

import { GAME_TUNING } from './gameConfig';
import type { SpawnWeight, StageDefinition, StageValidationResult } from './types';
import { isUsableHull } from '../utils/itemShape';

/**
 * 抽選テーブルを検証する。
 * - 空でないこと
 * - 重みが 0 以上の有限数であること
 * - 合計が期待値（既定 100）と一致すること
 * - レベルが重複しないこと
 */
export function validateSpawnWeights(
  weights: readonly SpawnWeight[],
  options: { expectedTotal?: number; tolerance?: number; maxLevel?: number } = {},
): StageValidationResult {
  const expectedTotal = options.expectedTotal ?? GAME_TUNING.spawn.expectedTotal;
  const tolerance = options.tolerance ?? GAME_TUNING.spawn.totalTolerance;
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!Array.isArray(weights) || weights.length === 0) {
    errors.push('抽選テーブルが空です。');
    return { ok: false, errors, warnings };
  }

  const seen = new Set<number>();
  let total = 0;

  for (const entry of weights) {
    if (!Number.isFinite(entry.level) || entry.level < 1 || !Number.isInteger(entry.level)) {
      errors.push(`抽選テーブルのレベルが不正です: ${String(entry.level)}`);
      continue;
    }
    if (seen.has(entry.level)) {
      errors.push(`抽選テーブルのレベルが重複しています: ${entry.level}`);
    }
    seen.add(entry.level);

    if (!Number.isFinite(entry.weight) || entry.weight < 0) {
      errors.push(`抽選確率が不正です (level ${entry.level}): ${String(entry.weight)}`);
      continue;
    }
    if (options.maxLevel !== undefined && entry.level > options.maxLevel) {
      errors.push(`抽選テーブルに存在しないレベルが含まれています: ${entry.level}`);
    }
    total += entry.weight;
  }

  if (total <= 0) {
    errors.push('抽選確率の合計が 0 です。');
  } else if (Math.abs(total - expectedTotal) > tolerance) {
    errors.push(`抽選確率の合計が ${expectedTotal} ではありません (実際: ${total})。`);
  }

  return { ok: errors.length === 0, errors, warnings };
}

/**
 * ステージ定義全体を検証する。
 * items のレベルが 1..N の連番になっていること等を確認する。
 */
export function validateStage(stage: StageDefinition): StageValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!stage.regionId) errors.push('regionId が未設定です。');
  if (!stage.title) errors.push('title が未設定です。');
  if (!Array.isArray(stage.items) || stage.items.length === 0) {
    errors.push('items が空です。');
    return { ok: false, errors, warnings };
  }

  const sorted = [...stage.items].sort((a, b) => a.level - b.level);
  const ids = new Set<string>();
  let previousRadius = 0;

  sorted.forEach((item, index) => {
    const expectedLevel = index + 1;
    if (item.level !== expectedLevel) {
      errors.push(
        `items のレベルが連番ではありません（期待: ${expectedLevel} / 実際: ${item.level}）。`,
      );
    }
    if (ids.has(item.id)) {
      errors.push(`items の id が重複しています: ${item.id}`);
    }
    ids.add(item.id);

    if (!item.name) errors.push(`name が未設定です: ${item.id}`);
    if (!(item.radius > 0)) errors.push(`radius が不正です: ${item.id}`);
    if (!(item.mass > 0)) errors.push(`mass が不正です: ${item.id}`);
    if (!(item.score >= 0)) errors.push(`score が不正です: ${item.id}`);
    if (!item.icon || !item.icon.label) errors.push(`icon.label が未設定です: ${item.id}`);

    if (item.hull && !isUsableHull(item.hull)) {
      warnings.push(`hull（輪郭）が不正なため円で扱います: ${item.id}`);
    }

    if (item.radius <= previousRadius) {
      warnings.push(`radius がレベル順に増えていません: ${item.id}`);
    }
    previousRadius = item.radius;
  });

  if (!sorted.some((item) => item.source)) {
    warnings.push('items に出典（source）が1件も設定されていません。');
  }

  warnings.push(...validateTour(stage, ids));

  const weightResult = validateSpawnWeights(
    stage.spawnWeights ?? GAME_TUNING.spawn.defaultWeights,
    {
      maxLevel: sorted.length,
    },
  );
  errors.push(...weightResult.errors);
  warnings.push(...weightResult.warnings);

  return { ok: errors.length === 0, errors, warnings };
}

/**
 * 背景ツアーを検証する。
 *
 * ツアーは任意の機能なので、**不備はすべて警告**にしてゲームは止めない。
 * ただし47県ぶん積み上がると人の目では追えなくなるため、機械で見ておく。
 */
function validateTour(stage: StageDefinition, itemIds: ReadonlySet<string>): string[] {
  const warnings: string[] = [];
  const tour = stage.scenery?.tour;
  if (!tour) return warnings;

  const sceneIds = new Set<string>();
  const hotspotIds = new Set<string>();

  tour.scenes.forEach((scene) => {
    if (sceneIds.has(scene.id)) warnings.push(`景色の id が重複しています: ${scene.id}`);
    sceneIds.add(scene.id);
    if (!scene.name) warnings.push(`景色の name が未設定です: ${scene.id}`);
    if (!scene.image) warnings.push(`景色の image が未設定です: ${scene.id}`);

    scene.hotspots.forEach((hotspot) => {
      if (hotspotIds.has(hotspot.id)) {
        warnings.push(`豆知識の id が重複しています: ${hotspot.id}`);
      }
      hotspotIds.add(hotspot.id);

      if (!(hotspot.x >= 0 && hotspot.x <= 1) || !(hotspot.y >= 0 && hotspot.y <= 1)) {
        warnings.push(`豆知識の座標が 0〜1 の外です: ${hotspot.id}`);
      }
      if (!hotspot.title) warnings.push(`豆知識の title が未設定です: ${hotspot.id}`);
      if (!hotspot.body) warnings.push(`豆知識の body が未設定です: ${hotspot.id}`);
      if (!hotspot.mapQuery) warnings.push(`豆知識の mapQuery が未設定です: ${hotspot.id}`);
      if (!hotspot.source) warnings.push(`豆知識に出典（source）がありません: ${hotspot.id}`);
      if (hotspot.relatedItemId && !itemIds.has(hotspot.relatedItemId)) {
        warnings.push(
          `豆知識の relatedItemId が存在しない料理を指しています: ${hotspot.id} -> ${hotspot.relatedItemId}`,
        );
      }
    });
  });

  return warnings;
}

/** 抽選テーブルを解決する（ステージ指定が無ければ既定値）。 */
export function resolveSpawnWeights(stage: StageDefinition): SpawnWeight[] {
  const weights = stage.spawnWeights ?? GAME_TUNING.spawn.defaultWeights;
  return weights.map((w) => ({ level: w.level, weight: w.weight }));
}
